import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, Save, UserPlus } from "lucide-react";

export default function ManagerAssignmentsPage() {
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // current manager being assigned
  const [chosen, setChosen] = useState(new Set());
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [mgrs, emps] = await Promise.all([
          api.get("/managers/list"),
          api.get("/employees"),
        ]);
        if (cancelled) return;
        setManagers(mgrs.data || []);
        setEmployees(emps.data || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const openAssign = (mgr) => {
    setEditing(mgr);
    setChosen(new Set((mgr.staff || []).map((s) => s.id)));
    setQ("");
  };

  const toggle = (id) => {
    setChosen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // Block double-assignment: employee can only be under one manager. Highlight
  // when picking someone who's currently under a different manager.
  const otherManagerByEmpId = useMemo(() => {
    const map = {};
    for (const m of managers) {
      for (const s of m.staff || []) {
        if (!editing || m.id !== editing.id) map[s.id] = m;
      }
    }
    return map;
  }, [managers, editing]);

  const filteredEmps = useMemo(() => {
    const t = q.trim().toLowerCase();
    return employees
      .filter((e) => !t ||
        (e.full_name || "").toLowerCase().includes(t) ||
        (e.employee_id || "").toLowerCase().includes(t) ||
        (e.department || "").toLowerCase().includes(t))
      .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
  }, [employees, q]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.post("/managers/assign", {
        user_id: editing.id,
        employee_ids: Array.from(chosen),
      });
      toast.success(`Assigned ${chosen.size} staff to ${editing.full_name}`);
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const unassigned = employees.filter((e) => !e.manager_user_id).length;

  return (
    <div className="space-y-6" data-testid="manager-assignments-page">
      <div>
        <div className="tiny-label">Access · scoping</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Manager assignments</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Assign staff to managers so each manager only sees and submits attendance for their own group.
        </p>
      </div>

      {!loading && managers.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-5 text-sm text-amber-900">
            No users with the <b>manager</b> role exist yet. Create a manager from{" "}
            <a className="underline font-medium" href="/users">User accounts</a> first.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 text-xs text-muted-foreground flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {employees.length} active employees</span>
          <Badge variant="outline">{unassigned} unassigned</Badge>
          <Badge variant="outline">{managers.length} managers</Badge>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {managers.map((m) => (
          <Card key={m.id} data-testid={`mgr-card-${m.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{m.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>
                <Badge variant="outline" className="shrink-0">{m.staff_count} staff</Badge>
              </div>
              <div className="flex flex-wrap gap-1">
                {(m.staff || []).slice(0, 8).map((s) => (
                  <Badge key={s.id} variant="secondary" className="text-xs font-normal">
                    {s.full_name}
                  </Badge>
                ))}
                {(m.staff || []).length > 8 && (
                  <Badge variant="outline" className="text-xs">+{(m.staff || []).length - 8} more</Badge>
                )}
                {(m.staff || []).length === 0 && (
                  <span className="text-xs text-muted-foreground italic">No staff assigned</span>
                )}
              </div>
              <Button size="sm" variant="outline" className="w-full"
                onClick={() => openAssign(m)} data-testid={`mgr-assign-${m.id}`}>
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Manage staff
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage staff for {editing?.full_name}</DialogTitle>
            <DialogDescription>
              Tick the employees this manager should see and submit attendance for.
              Each employee can only be assigned to one manager — selecting someone
              already under another manager will move them.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, ID, or department"
              className="pl-8"
              data-testid="mgr-emp-search"
            />
          </div>

          <div className="max-h-[60vh] overflow-y-auto border border-border rounded-md">
            {filteredEmps.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">No employees match.</div>
            )}
            {filteredEmps.map((e) => {
              const other = otherManagerByEmpId[e.id];
              const isChecked = chosen.has(e.id);
              return (
                <label
                  key={e.id}
                  className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 border-border hover:bg-muted/40 cursor-pointer"
                  data-testid={`mgr-emp-row-${e.id}`}
                >
                  <Checkbox checked={isChecked} onCheckedChange={() => toggle(e.id)} data-testid={`mgr-emp-cb-${e.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {e.employee_id} · {e.department || "—"}
                    </div>
                  </div>
                  {other && !isChecked && (
                    <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px]">
                      under {other.full_name}
                    </Badge>
                  )}
                  {other && isChecked && (
                    <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50 text-[10px]">
                      moves from {other.full_name}
                    </Badge>
                  )}
                </label>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="mgr-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="mgr-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : `Save (${chosen.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
